import React, { useState } from 'react';
import { CheckCircle2, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { products } from '../data/mock';
import RefinedOilsDetail from './RefinedOilsDetail';
import HighOleicDetail from './HighOleicDetail';
import OliveSqualeneDetail from './OliveSqualeneDetail';
import OliveOilDetail from './OliveOilDetail';
import CrudeGlycerinDetail from './CrudeGlycerinDetail';

const Products = () => {
  const [selectedProduct, setSelectedProduct] = useState(null);

  return (
    <section id="products" className="py-24 bg-gradient-to-b from-white to-amber-50/30">
      <div className="container mx-auto px-4">
        {/* Section Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Our Product Range
          </h2>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Comprehensive portfolio of premium edible oils and derivatives for diverse applications
          </p>
        </div>

        {/* Products List */}
        <div className="space-y-6 mb-16">
          {products.map((product) => (
            <ProductCard key={product.id} product={product} selectedProduct={selectedProduct} setSelectedProduct={setSelectedProduct} />
          ))}
        </div>
      </div>
    </section>
  );
};

const DETAIL_COMPONENTS = {
  1: HighOleicDetail,
  4: OliveSqualeneDetail,
  5: RefinedOilsDetail,
  6: OliveOilDetail,
  7: CrudeGlycerinDetail,
};

const ProductExpandedDetails = ({ product }) => {
  const DetailComponent = DETAIL_COMPONENTS[product.id];
  return (
    <div className="mt-6 pt-6 border-t space-y-6 animate-in slide-in-from-top-2">
      <div>
        <h4 className="font-semibold text-gray-900 mb-3 text-lg">Description</h4>
        <p className="text-gray-600 leading-relaxed">{product.description}</p>
      </div>

      {product.productVariants && (
        <div>
          <h4 className="font-semibold text-gray-900 mb-3 text-lg">Available Products</h4>
          <ul className="space-y-2">
            {product.productVariants.map((variant) => (
              <li key={variant} className="flex items-start">
                <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
                <span className="text-gray-600">{variant}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div>
        <h4 className="font-semibold text-gray-900 mb-3 text-lg">Key Features</h4>
        <ul className="space-y-2">
          {product.features.map((feature) => (
            <li key={feature} className="flex items-start">
              <CheckCircle2 className="w-5 h-5 text-green-600 mr-2 mt-0.5 flex-shrink-0" />
              <span className="text-gray-600">{feature}</span>
            </li>
          ))}
        </ul>
      </div>

      <div>
        <h4 className="font-semibold text-gray-900 mb-3 text-lg">Applications</h4>
        <div className="flex flex-wrap gap-2">
          {product.applications.map((app) => (
            <span 
              key={app}
              className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-sm font-medium"
            >
              {app}
            </span>
          ))}
        </div>
      </div>

      {product.specialNotes && (
        <div className="mt-6 p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border-2 border-blue-200">
          <h4 className="font-semibold text-gray-900 mb-3 text-lg flex items-center">
            <CheckCircle2 className="w-5 h-5 text-blue-600 mr-2" />
            {product.specialNotes.title}
          </h4>
          <p className="text-gray-700 leading-relaxed">
            {product.specialNotes.description}
          </p>
        </div>
      )}

      {DetailComponent && <DetailComponent product={product} />}
    </div>
  );
};

const ProductCard = ({ product, selectedProduct, setSelectedProduct }) => (
  <Card 
    className="group hover:shadow-2xl transition-all duration-300 overflow-hidden border-2 hover:border-amber-400"
  >
    <div className="flex flex-col md:flex-row">
      <div className="relative h-64 md:h-auto md:w-72 flex-shrink-0 overflow-hidden">
        <img 
          src={product.image}
          alt={product.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        />
        <div className="absolute inset-0 bg-gradient-to-t md:bg-gradient-to-r from-black/60 to-transparent"></div>
        <div className="absolute bottom-4 left-4 right-4 md:hidden">
          <h3 className="text-xl font-bold text-white drop-shadow-lg">
            {product.title}
          </h3>
        </div>
      </div>
      
      <CardContent className="p-6 flex-1">
        <h3 className="hidden md:block text-2xl font-bold text-gray-900 mb-3">
          {product.title}
        </h3>
        <p className="text-gray-600 mb-4 leading-relaxed">
          {product.shortDesc}
        </p>
        
        <Button 
          onClick={() => setSelectedProduct(product.id === selectedProduct ? null : product.id)}
          variant="outline"
          className="border-amber-500 text-amber-600 hover:bg-amber-50"
        >
          {selectedProduct === product.id ? 'Show Less' : 'Learn More'}
          <ArrowRight className="ml-2 w-4 h-4" />
        </Button>

        {/* Expanded Details */}
        {selectedProduct === product.id && (
          <ProductExpandedDetails product={product} />
        )}
      </CardContent>
    </div>
  </Card>
);

export default Products;
